/**
 * VSM shape and arrow contracts.
 *
 * These contracts describe the PostgreSQL-owned structured shape aggregate
 * and the semantic part of map arrows. They intentionally do not contain REST,
 * Prisma, Yjs node/arrow envelopes, endpoint ids, handles, or calculation
 * behaviour.
 *
 * The legacy oracle-shaped VSM contracts remain in `vsm.ts`. New consumers
 * must use the names in this file rather than repurposing `VSM_STATES`,
 * `VSM_CONNECTION_TYPES`, or `EDGE_KINDS`.
 */
export const VSM_SHAPE_SCHEMA_VERSION = 2;
export const VSM_CATALOGUE_VERSION = 2;
export const VSM_FIELD_DEFAULTS_VERSION = 1;
export const VSM_SHAPE_DATA_BOX_MAX_BYTES = 30_720;
export const VSM_SHAPE_DATA_BOX_MAX_DEPTH = 6;
export const VSM_TEXT_MAX_LENGTH = 2_000;
export const VSM_LABEL_MAX_LENGTH = 255;
export const VSM_ZONES = ["CURRENT", "FUTURE"];
/**
 * The five semantic lanes Player AI may name.
 *
 * These are product concepts rather than Yjs ids. The server resolves them to
 * the stable default-lane ids for the target zone, which keeps coordinates and
 * collaborative storage identities out of model-facing contracts.
 */
export const VSM_SEMANTIC_LANE_KEYS = [
    "customer_demand",
    "planning_information",
    "supplier_incoming",
    "core_production",
    "quality_rework_outbound",
];
export const VSM_SEMANTIC_LANE_LABELS = {
    customer_demand: "Customer & Demand",
    planning_information: "Planning & Information Flow",
    supplier_incoming: "Supplier & Incoming Material",
    core_production: "Core Production Flow",
    quality_rework_outbound: "Quality / Rework / Outbound",
};
export const VSM_STANDARD_LANE_IDS = {
    CURRENT: {
        customer_demand: "lane-default-1",
        planning_information: "lane-default-2",
        supplier_incoming: "lane-default-3",
        core_production: "lane-default-4",
        quality_rework_outbound: "lane-default-5",
    },
    FUTURE: {
        customer_demand: "lane-future-default-1",
        planning_information: "lane-future-default-2",
        supplier_incoming: "lane-future-default-3",
        core_production: "lane-future-default-4",
        quality_rework_outbound: "lane-future-default-5",
    },
};
export function vsmStandardLaneId(zone, lane) {
    return VSM_STANDARD_LANE_IDS[zone][lane];
}
export const VSM_GRID_ROW_PITCH = 440;
export const VSM_GRID_COLUMN_PITCH = 316;
export const VSM_GRID_PADDING = 24;
/** One column origin for the whole map, including manually moved lanes. */
export const VSM_GRID_COLUMN_ORIGIN = 328;
export function vsmGridLaneRows(height) {
    return Math.max(1, Math.floor((height - 2 * VSM_GRID_PADDING) / VSM_GRID_ROW_PITCH));
}
// --- Conversational map building (Player AI `vsm.map.propose_changes@4`) ----
/**
 * The semantic operations a new Run may propose.
 *
 * Each one says what a facilitator would say — "make sure there is a supplier
 * lane", "put a queue between pick and pack", "record the waiting time" — and
 * none of them names a coordinate, a handle or an arrow id. The product lowers
 * them onto its own plan; Collaboration turns the plan into geometry.
 */
export const VSM_MAP_OPERATIONS_V4 = [
    "ensure_lane",
    "ensure_boundary",
    "append_step",
    "insert_between",
    "update_data",
    "connect",
    "disconnect",
    "move_shape",
    "delete_shape",
    "reflow_section",
    "resize_lane",
];
/** Endpoints of a stream: found-or-created, never duplicated by accident. */
export const VSM_MAP_BOUNDARY_SHAPE_KINDS = [
    "CUSTOMER",
    "SUPPLIER",
    "OUTSIDE_SOURCE",
];
export const VSM_MAP_PROCESS_SHAPE_KINDS = [
    "DEDICATED_PROCESS",
    "WORKCELL",
];
/** The queue family: what sits between two steps and delays the second. */
export const VSM_MAP_QUEUE_SHAPE_KINDS = [
    "INVENTORY",
    "SUPERMARKET",
    "FIFO_LANE",
    "SAFETY_STOCK",
];
export const VSM_MAP_INFORMATION_CONTROL_SHAPE_KINDS = [
    "PRODUCTION_CONTROL",
    "INFORMATION_SYSTEM",
    "HEIJUNKA",
    "KANBAN_POST",
];
/**
 * AI-created process and queue shapes show their data box by default: the
 * numbers are the point of drawing them, and a box a person has to open by
 * hand is a box nobody reviews.
 */
export const VSM_MAP_AI_VISIBLE_DATA_BOX_KINDS = [
    ...VSM_MAP_PROCESS_SHAPE_KINDS,
    ...VSM_MAP_QUEUE_SHAPE_KINDS,
];
/**
 * Which standard lane a kind lands in when the proposal names none.
 *
 * Queues are the one kind this cannot answer alone: a queue belongs in the
 * lane of the step it delays, which the product resolves from the downstream
 * neighbour before falling back to this default.
 */
export function vsmDefaultLaneKeyForShapeKind(kind) {
    switch (kind) {
        case "CUSTOMER":
            return "customer_demand";
        case "SUPPLIER":
        case "OUTSIDE_SOURCE":
            return "supplier_incoming";
        case "PRODUCTION_CONTROL":
        case "INFORMATION_SYSTEM":
        case "HEIJUNKA":
        case "KANBAN_POST":
            return "planning_information";
        case "QUALITY_PROBLEM":
            return "quality_rework_outbound";
        default:
            return "core_production";
    }
}
/**
 * Normalize a lane or boundary label for find-or-create matching.
 *
 * Case, surrounding whitespace, punctuation and repeated spaces are not
 * differences a facilitator means: "Supplier – ACME Ltd." and "supplier acme
 * ltd" name the same endpoint.
 */
export function normalizeVsmMapLabel(label) {
    return label
        .normalize("NFKC")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();
}
/**
 * What `vsm.map.verify_layout` can report. Deterministic checks only — no
 * model is involved — and each code carries a bounded semantic repair the
 * model may propose next.
 */
export const VSM_MAP_LAYOUT_FINDING_CODES = [
    "UNPLACED_SHAPE",
    "UNASSIGNED_LANE",
    "DISCONNECTED_SHAPE",
    "DISCONNECTED_QUEUE",
    "INVALID_QUEUE_TOPOLOGY",
    "DUPLICATE_BOUNDARY",
    "BACKWARD_MATERIAL_FLOW",
    "OVERLAPPING_SHAPES",
    "LANE_OVERFLOW",
    "MISSING_SUPPLIER",
    "MISSING_CUSTOMER",
    "MISSING_INFORMATION_FLOW",
];
/** How the collaborative half of an applied map proposal actually went. */
export const VSM_CANVAS_DELIVERY_STATES = [
    "applied",
    "pending",
    "failed",
];
/**
 * Why a delivery is not `applied`, as a STABLE bounded code. Never an
 * infrastructure message: the receipt reaches a model and a card, and a raw
 * HTTP status or socket error belongs in the operator's outbox row only.
 */
export const VSM_CANVAS_DELIVERY_ERROR_CODES = [
    /** No outbox row exists for the command in this tenant. */
    "NOT_QUEUED",
    /** The product has no Collaboration delivery endpoint configured. */
    "NOT_CONFIGURED",
    /** The product has no delivery service at all; the outbox still holds it. */
    "DELIVERY_UNAVAILABLE",
    /** Another drain holds the command's lease right now. */
    "DELIVERY_IN_PROGRESS",
    /** Collaboration could not be reached or answered a retryable status. */
    "COLLAB_UNAVAILABLE",
    /** Collaboration refused the command; retrying would not change that. */
    "COLLAB_REJECTED",
    /** The durable retry budget ran out. */
    "DELIVERY_EXHAUSTED",
    /** An earlier drain deferred the command; the outbox still owes it. */
    "DELIVERY_DEFERRED",
    /** An earlier drain failed the command terminally. */
    "DELIVERY_FAILED",
    /** The accountable person discarded delivery; the saved change did not reach the canvas. */
    "DISCARDED",
];
export const VSM_SHAPE_CATEGORIES = [
    "PROCESS",
    "EXTERNAL_LOGISTICS",
    "INVENTORY_FLOW_CONTROL",
    "INFORMATION_PRODUCTION_CONTROL",
    "IMPROVEMENT",
    "ANNOTATION",
];
/**
 * Fixed V2 display order. The array order is presentation metadata only and
 * does not imply process, workshop, or connection order.
 */
export const VSM_SHAPE_KINDS = [
    "DEDICATED_PROCESS",
    "WORKCELL",
    "SUPPLIER",
    "CUSTOMER",
    "OUTSIDE_SOURCE",
    "CROSS_DOCK",
    "INVENTORY",
    "SUPERMARKET",
    "FIFO_LANE",
    "SAFETY_STOCK",
    "PRODUCTION_CONTROL",
    "INFORMATION_SYSTEM",
    "HEIJUNKA",
    "KANBAN_POST",
    "KAIZEN",
    "QUALITY_PROBLEM",
    "VALUE_STREAM_LOOP",
    "MILESTONE_FLAG",
];
/**
 * Map-zone placement policy. This is exhaustive so every new shape kind must
 * make an explicit Current/Future decision before it can compile.
 */
export const VSM_SHAPE_ZONE_CAPABILITIES = {
    DEDICATED_PROCESS: ["CURRENT", "FUTURE"],
    WORKCELL: ["CURRENT", "FUTURE"],
    SUPPLIER: ["CURRENT", "FUTURE"],
    CUSTOMER: ["CURRENT", "FUTURE"],
    OUTSIDE_SOURCE: ["CURRENT", "FUTURE"],
    CROSS_DOCK: ["CURRENT", "FUTURE"],
    INVENTORY: ["CURRENT", "FUTURE"],
    SUPERMARKET: ["CURRENT", "FUTURE"],
    FIFO_LANE: ["CURRENT", "FUTURE"],
    SAFETY_STOCK: ["CURRENT", "FUTURE"],
    PRODUCTION_CONTROL: ["CURRENT", "FUTURE"],
    INFORMATION_SYSTEM: ["CURRENT", "FUTURE"],
    HEIJUNKA: ["CURRENT", "FUTURE"],
    KANBAN_POST: ["CURRENT", "FUTURE"],
    KAIZEN: ["CURRENT"],
    QUALITY_PROBLEM: ["CURRENT"],
    VALUE_STREAM_LOOP: ["CURRENT", "FUTURE"],
    MILESTONE_FLAG: ["FUTURE"],
};
export function isVsmShapeAllowedInZone(kind, zone) {
    return /* SAFETY: The checked literal collection contains only values of the asserted member type. */ VSM_SHAPE_ZONE_CAPABILITIES[kind].includes(zone);
}
/** Stakeholder-approved shapes whose data boxes may carry VSM-scoped custom values. */
export const VSM_CUSTOM_FIELD_SHAPE_KINDS = [
    "DEDICATED_PROCESS",
    "WORKCELL",
    "INVENTORY",
    "FIFO_LANE",
    "SAFETY_STOCK",
    "CUSTOMER",
    "SUPPLIER",
    "OUTSIDE_SOURCE",
    "KANBAN_POST",
];
// --- Reusable values --------------------------------------------------------
export const VSM_DURATION_UNITS = [
    "MILLISECOND",
    "SECOND",
    "MINUTE",
    "HOUR",
    "DAY",
    "WEEK",
];
export const VSM_STANDARD_UNIT_KEYS = [
    "ITEM",
    "PIECE",
    "SET",
    "BATCH",
    "CONTAINER",
    "PALLET",
    "ORDER",
];
// --- Controlled data-box values --------------------------------------------
export const VSM_INVENTORY_POSITIONS = [
    "RAW_MATERIAL",
    "WORK_IN_PROCESS",
    "FINISHED_GOODS",
];
export const VSM_INVENTORY_PURPOSES = [
    "BUFFER_STOCK",
    "SAFETY_STOCK",
    "SHIPPING_STOCK",
];
export const VSM_CONTROL_METHODS = [
    "PUSH",
    "SUPERMARKET_PULL",
    "SEQUENTIAL_PULL",
    "MIXED_PULL",
];
export const VSM_SYSTEM_TYPES = [
    "ERP",
    "MRP",
    "MES",
    "WMS",
    "APS",
    "QMS",
    "SPREADSHEET",
    "OTHER",
];
export const VSM_KANBAN_TYPES = ["PRODUCTION", "WITHDRAWAL", "SIGNAL"];
export const VSM_SIGNAL_MEDIA = [
    "CARD",
    "CONTAINER",
    "BOARD",
    "PHYSICAL_MARKER",
    "ELECTRONIC",
    "OTHER",
];
export const VSM_IMPROVEMENT_STATUSES = [
    "OPEN",
    "PLANNED",
    "IN_PROGRESS",
    "BLOCKED",
    "DEFERRED",
    "DECLINED",
    "DONE",
];
// --- Typed custom fields ----------------------------------------------------
export const VSM_CUSTOM_FIELD_TYPES = [
    "TEXT",
    "BOOLEAN",
    "NUMBER",
    "COUNT",
    "PERCENTAGE",
    "DURATION",
    "QUANTITY",
    "MEASUREMENT",
    "RATE",
    "FREQUENCY",
    "DATE",
];
// --- Standard data-box fields ----------------------------------------------
export const VSM_DURATION_STANDARD_FIELDS = [
    "processingTime",
    "valueAddedTime",
    "cycleTime",
    "waitingTime",
    "changeoverTime",
    "availableTimePerShift",
    "epei",
    "orderLeadTime",
    "forecastHorizon",
    "replenishmentLeadTime",
    "targetCoverage",
    "planningHorizon",
    "frozenHorizon",
    "latency",
    "withdrawalInterval",
    "levelingHorizon",
];
export const VSM_QUANTITY_STANDARD_FIELDS = [
    "cycleOutputQuantity",
    "batchSize",
    "standardWip",
    "minimumOrderQuantity",
    "productionBatchSize",
    "packOutQuantity",
    "inventoryQuantity",
    "storageCapacity",
    "handlingBatchSize",
    "standardQuantity",
    "containerQuantity",
    "maximumQuantity",
    "triggerQuantity",
];
export const VSM_RATE_STANDARD_FIELDS = [
    "demandRate",
    "throughputRate",
];
export const VSM_FREQUENCY_STANDARD_FIELDS = [
    "orderFrequency",
    "releaseFrequency",
    "scheduleFrequency",
    "updateFrequency",
    "collectionFrequency",
];
export const VSM_COUNT_STANDARD_FIELDS = [
    "operators",
    "shifts",
    "kanbanCount",
    "cardCount",
];
export const VSM_PERCENTAGE_STANDARD_FIELDS = [
    "uptime",
    "firstPassYield",
    "scrapRate",
    "reworkRate",
    "onTimeDelivery",
    "onTimeDeliveryTarget",
    "defectRate",
    "inventoryAccuracy",
    "availability",
    "dataAccuracy",
];
export const VSM_TEXT_STANDARD_FIELDS = [
    "riskDescription",
    "productMixPattern",
    "opportunity",
    "targetCondition",
    "expectedImpact",
    "note",
    "problemStatement",
    "currentCondition",
    "containment",
];
export const VSM_BOOLEAN_STANDARD_FIELDS = [
    "shared",
    "inFutureStateDesign",
    "inImplementationPlan",
];
export const VSM_OWNER_STANDARD_FIELDS = ["owner"];
export const VSM_DATE_STANDARD_FIELDS = ["targetDate"];
export const VSM_CONTROLLED_STANDARD_FIELDS = [
    "inventoryPosition",
    "inventoryPurpose",
    "controlMethod",
    "systemType",
    "kanbanType",
    "signalMedium",
    "status",
];
export const VSM_STANDARD_FIELD_KEYS = [
    ...VSM_DURATION_STANDARD_FIELDS,
    ...VSM_QUANTITY_STANDARD_FIELDS,
    ...VSM_RATE_STANDARD_FIELDS,
    ...VSM_FREQUENCY_STANDARD_FIELDS,
    ...VSM_COUNT_STANDARD_FIELDS,
    ...VSM_PERCENTAGE_STANDARD_FIELDS,
    ...VSM_TEXT_STANDARD_FIELDS,
    ...VSM_BOOLEAN_STANDARD_FIELDS,
    ...VSM_OWNER_STANDARD_FIELDS,
    ...VSM_DATE_STANDARD_FIELDS,
    ...VSM_CONTROLLED_STANDARD_FIELDS,
];
/**
 * The designated metric-triple fields (PB-4888/4889, PB-365 AC2). The field's
 * stored value stays the "now" reading — every existing consumer is untouched —
 * while `metricTriples` carries the baseline / future target / lock beside it.
 * Custom fields never get triples.
 */
export const VSM_METRIC_TRIPLE_FIELDS = [
    "cycleTime",
    "waitingTime",
    "firstPassYield",
];
/** Which direction improves the measure — the node's delta reads from here. */
export const VSM_METRIC_TRIPLE_DIRECTIONS = {
    cycleTime: "lower",
    waitingTime: "lower",
    firstPassYield: "higher",
};
/**
 * Which way a numeric measure has to move for the future state to be BETTER.
 *
 * Compare reads this: without it a delta of −4 renders the same whether the
 * measure was cycle time (better) or first-pass yield (worse), which is PB-5244.
 *
 * `neutral` is a real answer, not a gap. A planning parameter, a batch size, a
 * frequency or a head-count is a CHOICE — moving it is neither a gain nor a
 * loss on its own — and calling one of them an improvement would put a verdict
 * on a decision the map does not make.
 *
 * `valueAddedTime` is neutral for a sharper reason: it is a duration, but it is
 * the GOOD time. Marking it "lower" would render a genuine improvement as a
 * regression — this bug again. `pce` already carries that signal.
 *
 * The three metric-triple fields are SPREAD IN from
 * `VSM_METRIC_TRIPLE_DIRECTIONS` rather than restated, so the two maps cannot
 * disagree about `cycleTime`, `waitingTime` or `firstPassYield`. Custom fields
 * are not in the union and read neutral by construction.
 */
export const VSM_FIELD_IMPROVEMENT_DIRECTIONS = {
    ...VSM_METRIC_TRIPLE_DIRECTIONS,
    // Durations — time the customer waits through.
    processingTime: "lower",
    changeoverTime: "lower",
    epei: "lower",
    orderLeadTime: "lower",
    replenishmentLeadTime: "lower",
    latency: "lower",
    // Durations that are settings, not outcomes.
    valueAddedTime: "neutral",
    availableTimePerShift: "neutral",
    forecastHorizon: "neutral",
    targetCoverage: "neutral",
    planningHorizon: "neutral",
    frozenHorizon: "neutral",
    withdrawalInterval: "neutral",
    levelingHorizon: "neutral",
    // Quantities — only stock on hand is waste; the rest size a container, a
    // batch or a shelf.
    inventoryQuantity: "lower",
    cycleOutputQuantity: "neutral",
    batchSize: "neutral",
    standardWip: "neutral",
    minimumOrderQuantity: "neutral",
    productionBatchSize: "neutral",
    packOutQuantity: "neutral",
    storageCapacity: "neutral",
    handlingBatchSize: "neutral",
    standardQuantity: "neutral",
    containerQuantity: "neutral",
    maximumQuantity: "neutral",
    triggerQuantity: "neutral",
    // Rates.
    throughputRate: "higher",
    demandRate: "neutral",
    // Frequencies — a cadence, chosen to fit the flow.
    orderFrequency: "neutral",
    releaseFrequency: "neutral",
    scheduleFrequency: "neutral",
    updateFrequency: "neutral",
    collectionFrequency: "neutral",
    // Counts — resourcing decisions.
    operators: "neutral",
    shifts: "neutral",
    kanbanCount: "neutral",
    cardCount: "neutral",
    // Percentages.
    uptime: "higher",
    onTimeDelivery: "higher",
    inventoryAccuracy: "higher",
    availability: "higher",
    dataAccuracy: "higher",
    scrapRate: "lower",
    reworkRate: "lower",
    defectRate: "lower",
    // A target is the bar someone set, not a measurement of the stream.
    onTimeDeliveryTarget: "neutral",
};
/**
 * The direction a numeric standard field improves in, or `"neutral"` for a
 * custom field, a text field, or anything the map does not measure.
 *
 * `Object.hasOwn`, not `in` — a custom field named `toString` or `constructor`
 * would otherwise resolve to a prototype member and be returned as a direction.
 */
export function vsmImprovementDirection(field) {
    return Object.hasOwn(VSM_FIELD_IMPROVEMENT_DIRECTIONS, field)
        ? VSM_FIELD_IMPROVEMENT_DIRECTIONS[
        /* SAFETY: The surrounding guard or owned contract establishes the asserted shape. */ field]
        : "neutral";
}
export const VSM_SHAPE_PROFILES = {
    DEDICATED_PROCESS: {
        defaultVisibleFields: [
            "cycleTime",
            "changeoverTime",
            "uptime",
            "operators",
        ],
        optionalRecognizedFields: [
            "processingTime",
            "valueAddedTime",
            "waitingTime",
            "availableTimePerShift",
            "shifts",
            "cycleOutputQuantity",
            "batchSize",
            "epei",
            "firstPassYield",
            "scrapRate",
            "reworkRate",
            "shared",
        ],
    },
    WORKCELL: {
        defaultVisibleFields: ["cycleTime", "operators", "uptime", "standardWip"],
        optionalRecognizedFields: [
            "processingTime",
            "valueAddedTime",
            "waitingTime",
            "changeoverTime",
            "availableTimePerShift",
            "shifts",
            "cycleOutputQuantity",
            "batchSize",
            "epei",
            "firstPassYield",
            "scrapRate",
            "reworkRate",
        ],
    },
    SUPPLIER: {
        defaultVisibleFields: [
            "orderLeadTime",
            "minimumOrderQuantity",
            "onTimeDelivery",
        ],
        optionalRecognizedFields: ["productionBatchSize", "epei", "defectRate"],
    },
    CUSTOMER: {
        defaultVisibleFields: ["demandRate", "packOutQuantity"],
        optionalRecognizedFields: [
            "shifts",
            "availableTimePerShift",
            "orderFrequency",
            "forecastHorizon",
            "onTimeDeliveryTarget",
        ],
    },
    OUTSIDE_SOURCE: {
        defaultVisibleFields: ["orderLeadTime", "demandRate", "onTimeDelivery"],
        optionalRecognizedFields: [],
    },
    CROSS_DOCK: {
        defaultVisibleFields: ["throughputRate", "waitingTime"],
        optionalRecognizedFields: [
            "handlingBatchSize",
            "operators",
            "shifts",
            "uptime",
            "firstPassYield",
        ],
    },
    INVENTORY: {
        defaultVisibleFields: ["inventoryQuantity", "waitingTime"],
        optionalRecognizedFields: [
            "inventoryPosition",
            "inventoryPurpose",
            "storageCapacity",
            "inventoryAccuracy",
        ],
    },
    SUPERMARKET: {
        defaultVisibleFields: [
            "inventoryQuantity",
            "standardQuantity",
            "containerQuantity",
        ],
        optionalRecognizedFields: [
            "kanbanCount",
            "replenishmentLeadTime",
            "waitingTime",
            "storageCapacity",
            "inventoryAccuracy",
        ],
    },
    FIFO_LANE: {
        defaultVisibleFields: ["inventoryQuantity", "maximumQuantity"],
        optionalRecognizedFields: ["waitingTime", "releaseFrequency"],
    },
    SAFETY_STOCK: {
        defaultVisibleFields: ["inventoryQuantity"],
        optionalRecognizedFields: [
            "targetCoverage",
            "riskDescription",
            "waitingTime",
        ],
    },
    PRODUCTION_CONTROL: {
        defaultVisibleFields: ["planningHorizon", "scheduleFrequency"],
        optionalRecognizedFields: [
            "releaseFrequency",
            "frozenHorizon",
            "controlMethod",
        ],
    },
    INFORMATION_SYSTEM: {
        defaultVisibleFields: ["updateFrequency", "latency"],
        optionalRecognizedFields: ["systemType", "availability", "dataAccuracy"],
    },
    HEIJUNKA: {
        defaultVisibleFields: ["withdrawalInterval", "packOutQuantity"],
        optionalRecognizedFields: [
            "levelingHorizon",
            "productMixPattern",
            "cardCount",
        ],
    },
    KANBAN_POST: {
        defaultVisibleFields: ["kanbanType", "cardCount", "containerQuantity"],
        optionalRecognizedFields: [
            "triggerQuantity",
            "collectionFrequency",
            "signalMedium",
        ],
    },
    // Two-artifact model: a Kaizen burst is an OPPORTUNITY and carries no
    // lifecycle of its own. `status`, `targetDate`, `outcome` and
    // `didAnythingChange` were removed together with their schema entries — the
    // drift guard requires profile and schema to agree, and the last two existed
    // only to be required when status was DONE, a state nothing can reach now.
    // Execution state lives on the Implementation Plan row a Kaizen becomes at
    // promotion. `status`/`targetDate` survive in the shared field registry for
    // QUALITY_PROBLEM, which still carries them.
    KAIZEN: {
        defaultVisibleFields: ["opportunity", "targetCondition"],
        optionalRecognizedFields: [
            "owner",
            "expectedImpact",
            "inFutureStateDesign",
            "inImplementationPlan",
        ],
    },
    QUALITY_PROBLEM: {
        defaultVisibleFields: ["problemStatement", "currentCondition"],
        optionalRecognizedFields: [
            "targetCondition",
            "defectRate",
            "firstPassYield",
            "containment",
            "owner",
            "targetDate",
            "status",
        ],
    },
    VALUE_STREAM_LOOP: {
        defaultVisibleFields: ["note"],
        optionalRecognizedFields: [],
    },
    MILESTONE_FLAG: {
        defaultVisibleFields: ["note"],
        optionalRecognizedFields: [],
    },
};
export function getVsmRecognizedStandardFields(kind) {
    const profile = VSM_SHAPE_PROFILES[kind];
    return [...profile.defaultVisibleFields, ...profile.optionalRecognizedFields];
}
// --- Calculation eligibility -----------------------------------------------
export const VSM_CALCULATION_ROLES = [
    "TAKT_DEMAND_RATE",
    "TAKT_AVAILABLE_TIME",
    "PITCH_PACK_OUT_QUANTITY",
    "INVENTORY_COVERAGE_QUANTITY",
    "INVENTORY_COVERAGE_RATE",
    "PROCESS_TIME_TOTAL",
    "WAIT_TIME_TOTAL",
    "LEAD_TIME_TOTAL",
    "PCE_VALUE_ADDED_TIME",
    "CAPACITY_CYCLE_TIME",
    "CAPACITY_CYCLE_OUTPUT_QUANTITY",
    "CAPACITY_AVAILABLE_TIME",
    "CAPACITY_UPTIME",
    "CAPACITY_OPERATORS",
    "CAPACITY_SHIFTS",
    "CAPACITY_THROUGHPUT_RATE",
    "QUALITY_FIRST_PASS_YIELD",
    "QUALITY_SCRAP_RATE",
    "QUALITY_REWORK_RATE",
    "QUALITY_DEFECT_RATE",
    "QUALITY_DELIVERY",
    "QUALITY_INVENTORY_ACCURACY",
    "QUALITY_AVAILABILITY",
    "QUALITY_DATA_ACCURACY",
];
/**
 * Eligibility is not selection: later C7 work decides which eligible source,
 * path, scope, and conversion feed a calculation.
 */
export const VSM_CALCULATION_ELIGIBILITY = {
    processingTime: [],
    valueAddedTime: ["PCE_VALUE_ADDED_TIME"],
    cycleTime: ["PROCESS_TIME_TOTAL", "LEAD_TIME_TOTAL", "CAPACITY_CYCLE_TIME"],
    waitingTime: ["WAIT_TIME_TOTAL", "LEAD_TIME_TOTAL"],
    changeoverTime: [],
    availableTimePerShift: ["TAKT_AVAILABLE_TIME", "CAPACITY_AVAILABLE_TIME"],
    epei: [],
    orderLeadTime: ["LEAD_TIME_TOTAL"],
    forecastHorizon: [],
    replenishmentLeadTime: ["LEAD_TIME_TOTAL"],
    targetCoverage: [],
    planningHorizon: [],
    frozenHorizon: [],
    latency: [],
    withdrawalInterval: [],
    levelingHorizon: [],
    cycleOutputQuantity: ["CAPACITY_CYCLE_OUTPUT_QUANTITY"],
    batchSize: [],
    standardWip: [],
    minimumOrderQuantity: [],
    productionBatchSize: [],
    packOutQuantity: ["PITCH_PACK_OUT_QUANTITY"],
    inventoryQuantity: ["INVENTORY_COVERAGE_QUANTITY"],
    storageCapacity: [],
    handlingBatchSize: [],
    standardQuantity: [],
    containerQuantity: [],
    maximumQuantity: [],
    triggerQuantity: [],
    demandRate: ["TAKT_DEMAND_RATE", "INVENTORY_COVERAGE_RATE"],
    throughputRate: ["INVENTORY_COVERAGE_RATE", "CAPACITY_THROUGHPUT_RATE"],
    orderFrequency: [],
    releaseFrequency: [],
    scheduleFrequency: [],
    updateFrequency: [],
    collectionFrequency: [],
    operators: ["CAPACITY_OPERATORS"],
    shifts: ["TAKT_AVAILABLE_TIME", "CAPACITY_SHIFTS"],
    kanbanCount: [],
    cardCount: [],
    uptime: ["CAPACITY_UPTIME"],
    firstPassYield: ["QUALITY_FIRST_PASS_YIELD"],
    scrapRate: ["QUALITY_SCRAP_RATE"],
    reworkRate: ["QUALITY_REWORK_RATE"],
    onTimeDelivery: ["QUALITY_DELIVERY"],
    onTimeDeliveryTarget: ["QUALITY_DELIVERY"],
    defectRate: ["QUALITY_DEFECT_RATE"],
    inventoryAccuracy: ["QUALITY_INVENTORY_ACCURACY"],
    availability: ["QUALITY_AVAILABILITY"],
    dataAccuracy: ["QUALITY_DATA_ACCURACY"],
    riskDescription: [],
    productMixPattern: [],
    opportunity: [],
    targetCondition: [],
    expectedImpact: [],
    note: [],
    problemStatement: [],
    currentCondition: [],
    containment: [],
    shared: [],
    inFutureStateDesign: [],
    inImplementationPlan: [],
    owner: [],
    targetDate: [],
    inventoryPosition: [],
    inventoryPurpose: [],
    controlMethod: [],
    systemType: [],
    kanbanType: [],
    signalMedium: [],
    status: [],
};
// --- Semantic flow contracts -----------------------------------------------
export const VSM_FLOW_KINDS = ["MATERIAL", "INFORMATION"];
export const VSM_MATERIAL_FLOW_MODES = [
    "CONTINUOUS_FLOW",
    "PUSH",
    "PULL",
    "TRANSPORT",
    "SHIPMENT",
    "MILK_RUN",
    "REWORK",
];
export const VSM_INFORMATION_FLOW_MODES = [
    "MANUAL",
    "ELECTRONIC",
    "VERBAL",
    "GO_SEE",
];
export const VSM_INFORMATION_SIGNALS = [
    "FORECAST",
    "CUSTOMER_ORDER",
    "PRODUCTION_SCHEDULE",
    "SHIPPING_SCHEDULE",
    "PRODUCTION_KANBAN",
    "WITHDRAWAL_KANBAN",
    "SIGNAL_KANBAN",
    "SEQUENCED_PULL",
    "EXPEDITE",
];
/** Information signals for which a batch-arrival marker is meaningful. */
export const VSM_BATCH_ARRIVAL_SIGNALS = [
    "PRODUCTION_KANBAN",
    "WITHDRAWAL_KANBAN",
    "SIGNAL_KANBAN",
];
export const VSM_FLOW_DIRECTIONS = ["INBOUND", "OUTBOUND"];
const FLOW_BOTH = ["INBOUND", "OUTBOUND"];
const FLOW_NONE = [];
export const VSM_SHAPE_FLOW_CAPABILITIES = {
    DEDICATED_PROCESS: { MATERIAL: FLOW_BOTH, INFORMATION: FLOW_BOTH },
    WORKCELL: { MATERIAL: FLOW_BOTH, INFORMATION: FLOW_BOTH },
    SUPPLIER: { MATERIAL: ["OUTBOUND"], INFORMATION: FLOW_BOTH },
    CUSTOMER: { MATERIAL: ["INBOUND"], INFORMATION: FLOW_BOTH },
    OUTSIDE_SOURCE: { MATERIAL: FLOW_BOTH, INFORMATION: FLOW_BOTH },
    CROSS_DOCK: { MATERIAL: FLOW_BOTH, INFORMATION: FLOW_BOTH },
    INVENTORY: { MATERIAL: FLOW_BOTH, INFORMATION: ["OUTBOUND"] },
    SUPERMARKET: { MATERIAL: FLOW_BOTH, INFORMATION: FLOW_BOTH },
    FIFO_LANE: { MATERIAL: FLOW_BOTH, INFORMATION: ["OUTBOUND"] },
    SAFETY_STOCK: { MATERIAL: FLOW_BOTH, INFORMATION: ["OUTBOUND"] },
    PRODUCTION_CONTROL: { MATERIAL: FLOW_NONE, INFORMATION: FLOW_BOTH },
    INFORMATION_SYSTEM: { MATERIAL: FLOW_NONE, INFORMATION: FLOW_BOTH },
    HEIJUNKA: { MATERIAL: FLOW_NONE, INFORMATION: FLOW_BOTH },
    KANBAN_POST: { MATERIAL: FLOW_NONE, INFORMATION: FLOW_BOTH },
    KAIZEN: { MATERIAL: FLOW_NONE, INFORMATION: FLOW_NONE },
    QUALITY_PROBLEM: { MATERIAL: FLOW_NONE, INFORMATION: FLOW_NONE },
    VALUE_STREAM_LOOP: { MATERIAL: FLOW_NONE, INFORMATION: FLOW_NONE },
    MILESTONE_FLAG: { MATERIAL: FLOW_NONE, INFORMATION: FLOW_NONE },
};
export const VSM_FLOW_REJECTION_REASONS = [
    "CROSS_MAP_STATE",
    "SOURCE_FLOW_NOT_SUPPORTED",
    "TARGET_FLOW_NOT_SUPPORTED",
    "CONTINUOUS_FLOW_REQUIRES_PROCESS_ENDPOINTS",
    "REWORK_REQUIRES_PROCESS_ENDPOINTS",
    "EXTERNAL_LOGISTICS_ENDPOINT_REQUIRED",
];
export const VSM_SHAPE_CATALOGUE = [
    {
        kind: "DEDICATED_PROCESS",
        category: "PROCESS",
        label: "Dedicated Process",
        profile: VSM_SHAPE_PROFILES.DEDICATED_PROCESS,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.DEDICATED_PROCESS,
    },
    {
        kind: "WORKCELL",
        category: "PROCESS",
        label: "Workcell",
        profile: VSM_SHAPE_PROFILES.WORKCELL,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.WORKCELL,
    },
    {
        kind: "SUPPLIER",
        category: "EXTERNAL_LOGISTICS",
        label: "Supplier",
        profile: VSM_SHAPE_PROFILES.SUPPLIER,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.SUPPLIER,
    },
    {
        kind: "CUSTOMER",
        category: "EXTERNAL_LOGISTICS",
        label: "Customer",
        profile: VSM_SHAPE_PROFILES.CUSTOMER,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.CUSTOMER,
    },
    {
        kind: "OUTSIDE_SOURCE",
        category: "EXTERNAL_LOGISTICS",
        label: "Outside Source",
        profile: VSM_SHAPE_PROFILES.OUTSIDE_SOURCE,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.OUTSIDE_SOURCE,
    },
    {
        kind: "CROSS_DOCK",
        category: "EXTERNAL_LOGISTICS",
        label: "Cross Dock",
        profile: VSM_SHAPE_PROFILES.CROSS_DOCK,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.CROSS_DOCK,
    },
    {
        kind: "INVENTORY",
        category: "INVENTORY_FLOW_CONTROL",
        label: "Inventory",
        profile: VSM_SHAPE_PROFILES.INVENTORY,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.INVENTORY,
    },
    {
        kind: "SUPERMARKET",
        category: "INVENTORY_FLOW_CONTROL",
        label: "Supermarket",
        profile: VSM_SHAPE_PROFILES.SUPERMARKET,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.SUPERMARKET,
    },
    {
        kind: "FIFO_LANE",
        category: "INVENTORY_FLOW_CONTROL",
        label: "FIFO Lane",
        profile: VSM_SHAPE_PROFILES.FIFO_LANE,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.FIFO_LANE,
    },
    {
        kind: "SAFETY_STOCK",
        category: "INVENTORY_FLOW_CONTROL",
        label: "Safety Stock",
        profile: VSM_SHAPE_PROFILES.SAFETY_STOCK,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.SAFETY_STOCK,
    },
    {
        kind: "PRODUCTION_CONTROL",
        category: "INFORMATION_PRODUCTION_CONTROL",
        label: "Production Control",
        profile: VSM_SHAPE_PROFILES.PRODUCTION_CONTROL,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.PRODUCTION_CONTROL,
    },
    {
        kind: "INFORMATION_SYSTEM",
        category: "INFORMATION_PRODUCTION_CONTROL",
        label: "Information System",
        profile: VSM_SHAPE_PROFILES.INFORMATION_SYSTEM,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.INFORMATION_SYSTEM,
    },
    {
        kind: "HEIJUNKA",
        category: "INFORMATION_PRODUCTION_CONTROL",
        label: "Heijunka",
        profile: VSM_SHAPE_PROFILES.HEIJUNKA,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.HEIJUNKA,
    },
    {
        kind: "KANBAN_POST",
        category: "INFORMATION_PRODUCTION_CONTROL",
        label: "Kanban Post",
        profile: VSM_SHAPE_PROFILES.KANBAN_POST,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.KANBAN_POST,
    },
    {
        kind: "KAIZEN",
        category: "IMPROVEMENT",
        label: "Kaizen",
        profile: VSM_SHAPE_PROFILES.KAIZEN,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.KAIZEN,
    },
    {
        kind: "QUALITY_PROBLEM",
        category: "IMPROVEMENT",
        label: "Quality Problem",
        profile: VSM_SHAPE_PROFILES.QUALITY_PROBLEM,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.QUALITY_PROBLEM,
    },
    {
        kind: "VALUE_STREAM_LOOP",
        category: "ANNOTATION",
        label: "Value Stream Loop",
        profile: VSM_SHAPE_PROFILES.VALUE_STREAM_LOOP,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.VALUE_STREAM_LOOP,
    },
    {
        kind: "MILESTONE_FLAG",
        category: "ANNOTATION",
        label: "Milestone Flag",
        profile: VSM_SHAPE_PROFILES.MILESTONE_FLAG,
        flowCapabilities: VSM_SHAPE_FLOW_CAPABILITIES.MILESTONE_FLAG,
    },
];
// --- Map footprint (PB-5441 UX-6) ------------------------------------------
/** Height of a placed symbol's glyph node, in map px. */
export const VSM_SYMBOL_HEIGHT = 116;
/** Band under the glyph that holds the step's name and caption. */
export const VSM_SYMBOL_NAME_BAND = 90;
/** Gap between the name band and the data box. */
export const VSM_DATA_BOX_GAP = 24;
/** The data box's padding, top and bottom together. */
export const VSM_DATA_BOX_PADDING_Y = 16;
/** One key-fact row of the data box. */
export const VSM_DATA_BOX_ROW_HEIGHT = 34;
/** Widest data box: neighbours one 316 px placement pitch apart never touch. */
export const VSM_DATA_BOX_MAX_WIDTH = 260;
const VSM_LANE_FREE_CATEGORIES = new Set([
    "IMPROVEMENT",
    "ANNOTATION",
]);
const VSM_SHAPE_CATEGORY_BY_KIND = new Map(VSM_SHAPE_CATALOGUE.map((item) => [item.kind, item.category]));
/** IMPROVEMENT and ANNOTATION symbols sit outside lanes and draw no data box. */
export function isVsmLaneFreeShapeKind(kind) {
    const category = VSM_SHAPE_CATEGORY_BY_KIND.get(kind);
    return category !== undefined && VSM_LANE_FREE_CATEGORIES.has(category);
}
/** Height of the data box band (gap + box) for a kind: one row per key fact. */
export function vsmDataBoxBandHeight(kind) {
    const rows = VSM_SHAPE_PROFILES[kind].defaultVisibleFields.length;
    return (VSM_DATA_BOX_GAP + VSM_DATA_BOX_PADDING_Y + rows * VSM_DATA_BOX_ROW_HEIGHT);
}
/**
 * The vertical room a placed symbol needs in its lane: glyph, name band and,
 * when its "Show on map" switch is on, the data box band. It reads only what
 * the Yjs entry holds (the kind hint and `showDataBox`), so the browser and
 * collab compute the same room for the same entry. An unknown kind gets no
 * data box band.
 */
export function vsmSymbolRoomHeight(kind, showDataBox) {
    const base = VSM_SYMBOL_HEIGHT + VSM_SYMBOL_NAME_BAND;
    if (!showDataBox || kind === null || isVsmLaneFreeShapeKind(kind))
        return base;
    return base + vsmDataBoxBandHeight(kind);
}
//# sourceMappingURL=vsm-map-contracts.js.map